import { useEffect, useState } from "react";

export default function TableWrapper({
    data = [],
    itemsPerPage = 5,
    filterItem,
    children,
}) {
    // Convert object to array if needed
    data = Array.isArray(data) ? data : Object?.keys(data).map(keyword => ({keyword, ...data[keyword]}));

    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredData, setFilteredData] = useState(data);
    const [currentPage, setCurrentPage] = useState(0);
    const [newData, setNewData] = useState([]);

    const numberOfSelection = itemsPerPage;

    const handlePrev = () => {
        if (currentPage !== 0) {
            setCurrentPage((prev) => prev - numberOfSelection);
        }
    };

    const handleNext = () => {
        if (currentPage < data.length) {
            setCurrentPage((prev) => prev + numberOfSelection);
        }
    };

    const handleSearch = ({ target: { value } }, name) => {
        setSearchTerm(value);
        let newFilteredData = data.filter((item) =>
            item[name]?.toLowerCase().startsWith(value.toLowerCase())
        );
        setFilteredData(newFilteredData);
        setCurrentPage(0);
    };

    useEffect(() => {
        setIsLoading(true);
        setTimeout(() => {
            setNewData(
                filteredData?.slice(currentPage, numberOfSelection + currentPage)
            );
            setIsLoading(false);
        }, 500);
    }, [currentPage, filteredData, numberOfSelection]);

    useEffect(() => {
        setCurrentPage(0);
    }, [itemsPerPage]);

    return (
        <div className="w-full bg-white rounded-2xl">
            {/* Search Section */}
            {data.length > 0 && filterItem && (
                <div className="p-4 border-b border-gray-100">
                    <div className="flex flex-col sm:flex-row flex-wrap items-center gap-3">
                        {filterItem.map((item, index) => (
                            <div key={index} className="flex items-center gap-2 w-full sm:w-auto">
                                <label className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                    🔍 Search by {item.charAt(0).toUpperCase() + item.slice(1)}
                                </label>
                                <input
                                    name={item}
                                    type="text"
                                    className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#069125]/20 focus:border-[#069125] outline-none transition-all duration-200 w-full sm:w-40"
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e, item)}
                                    placeholder={`Search ${item}...`}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        
            <div className="w-full flex flex-col">
                {/* Table Content */}
                <div className="w-full">
                    {children({ data: newData })}
                </div>
                
                {/* Pagination */}
                {(data.length > itemsPerPage) && (
                    <div className='p-4 border-t border-gray-100 w-full flex flex-col sm:flex-row justify-between items-center gap-3'>
                        <div className="text-sm text-gray-500">
                            Showing <span className="font-semibold text-gray-700">
                                {isLoading ? '...' : `${currentPage + 1}-${Math.min(currentPage + numberOfSelection, data.length)}`}
                            </span> of <span className="font-semibold text-gray-700">{data.length}</span> entries
                        </div>
                        <div className='flex items-center gap-2'>
                            <button 
                                onClick={handlePrev}
                                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    currentPage === 0 
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                        : 'bg-[#069125] text-white hover:bg-[#057a1f] hover:shadow-md'
                                }`}
                                disabled={isLoading || currentPage === 0}
                            >
                                ◀ Previous
                            </button>
                            <span className="text-sm text-gray-500 px-2">
                                {Math.floor(currentPage / numberOfSelection) + 1}
                            </span>
                            <button 
                                onClick={handleNext}
                                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                    currentPage + numberOfSelection >= data.length 
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                                        : 'bg-[#069125] text-white hover:bg-[#057a1f] hover:shadow-md'
                                }`}
                                disabled={isLoading || currentPage + numberOfSelection >= data.length}
                            >
                                Next ▶
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Loading Overlay */}
            {isLoading && (
                <div className="w-full fixed z-[999] inset-0 flex justify-center items-center bg-black/10 backdrop-blur-sm">
                    <div className="rounded-2xl shadow-xl p-6 bg-white flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#069125]"></div>
                        <p className="text-sm text-gray-600 font-medium">Loading...</p>
                    </div>
                </div>
            )}
        </div>
    );
}